import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, updateProfile, updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getUserUsage, TIER_LIMITS } from '../services/usageTracking';
import NotificationSettings from '../components/NotificationSettings';
import DocumentManager from '../components/DocumentManager';
import { getSafeDisplayName } from '../utils/profanityFilter';

export default function Settings({ darkMode, onToggleTheme, user }) {
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    profile: true,
    subscription: true,
    usage: false,
    accent: false,
    appearance: false,
    notifications: false,
    documents: false
  });
  
  // Profile state
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  
  // Accent color state
  const [selectedAccentColor, setSelectedAccentColor] = useState(() => {
    return localStorage.getItem('fidora_accent_color') || 'purple';
  });

  const theme = {
    card: darkMode ? 'rgba(49, 46, 129, 0.4)' : 'rgba(255, 255, 255, 0.85)',
    border: darkMode ? 'rgba(224, 231, 255, 0.15)' : 'rgba(30, 41, 59, 0.1)',
    text: darkMode ? '#ffffff' : '#1e293b',
    subtext: darkMode ? '#e0e7ff' : '#64748b',
    accent: darkMode ? '#818cf8' : '#8b5cf6',
    bg: darkMode ? '#0f172a' : '#f8fafc',
    success: '#10b981',
    error: '#ef4444'
  };

  // Accent color options
  const accentColors = [
    { id: 'purple', name: 'Purple', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', primary: '#6366f1' },
    { id: 'blue', name: 'Blue', gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', primary: '#3b82f6' },
    { id: 'green', name: 'Green', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', primary: '#10b981' },
    { id: 'orange', name: 'Orange', gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', primary: '#f97316' },
    { id: 'pink', name: 'Pink', gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', primary: '#ec4899' },
    { id: 'teal', name: 'Teal', gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', primary: '#14b8a6' }
  ];

  useEffect(() => {
    loadProfile();
    if (user?.uid) {
      loadSubscriptionTier();
      loadUsage();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadProfile() {
    if (user) {
      setDisplayName(user.displayName || '');
      setEmail(user.email || '');
      setNewEmail(user.email || ''); // Initialize newEmail with current email
    }
  }

  async function loadSubscriptionTier() {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSubscriptionTier(data.subscriptionTier || 'free');
        setSubscriptionStatus(data.subscriptionStatus || 'active');
      }
    } catch (error) {
      console.error('Error loading subscription tier:', error);
    }
  }

  async function loadUsage() {
    if (!user?.uid) return;
    setLoadingUsage(true);
    try {
      const usageData = await getUserUsage(user.uid);
      setUsage(usageData);
    } catch (error) {
      console.error('Error loading usage:', error);
    } finally {
      setLoadingUsage(false);
    }
  }

  async function handleSaveDisplayName() {
    if (!user || !user.uid) return;
    setSavingProfile(true);
    setProfileError('');
    setProfileSuccess('');
    
    try {
      const auth = getAuth();
      const db = getFirestore();
      const userDocRef = doc(db, 'users', user.uid);
      
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, { displayName: displayName.trim() });
      
      // Check if user document exists in Firestore
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        // Update existing document
        await updateDoc(userDocRef, {
          displayName: displayName.trim(),
          lastUpdated: serverTimestamp()
        });
      } else {
        // Create new document if it doesn't exist
        await setDoc(userDocRef, {
          displayName: displayName.trim(),
          email: user.email || '',
          userId: user.uid,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        }, { merge: true });
      }
      
      setProfileSuccess('Display name updated successfully!');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating display name:', error);
      setProfileError(`Failed to update display name: ${error.message || 'Please try again.'}`);
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleUpdateEmail() {
    if (!user || !newEmail.trim()) return;
    setSavingProfile(true);
    setProfileError('');
    setProfileSuccess('');
    
    try {
      const auth = getAuth();
      await updateEmail(auth.currentUser, newEmail.trim());
      
      // Update Firestore
      const db = getFirestore();
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          email: newEmail.trim(),
          lastUpdated: serverTimestamp()
        });
      } else {
        await setDoc(userDocRef, {
          email: newEmail.trim(),
          displayName: user.displayName || '',
          userId: user.uid,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        }, { merge: true });
      }
      
      setEmail(newEmail.trim());
      setNewEmail(newEmail.trim()); // Keep the updated email in the field
      setProfileSuccess('Email updated successfully!');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating email:', error);
      if (error.code === 'auth/requires-recent-login') {
        setProfileError('Please sign out and sign back in to update your email.');
      } else {
        setProfileError('Failed to update email. Please try again.');
      }
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleChangePassword() {
    if (!user || !currentPassword || !newPassword) return;
    setSavingProfile(true);
    setProfileError('');
    setProfileSuccess('');
    
    if (newPassword !== confirmPassword) {
      setProfileError('New passwords do not match.');
      setSavingProfile(false);
      return;
    }
    
    if (newPassword.length < 6) {
      setProfileError('Password must be at least 6 characters.');
      setSavingProfile(false);
      return;
    }
    
    try {
      const auth = getAuth();
      const userCred = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, userCred);
      await updatePassword(auth.currentUser, newPassword);
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setProfileSuccess('Password changed successfully!');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setProfileError('Current password is incorrect.');
      } else if (error.code === 'auth/weak-password') {
        setProfileError('New password is too weak. Please use a stronger password.');
      } else {
        setProfileError('Failed to change password. Please try again.');
      }
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleOpenStripePortal() {
    try {
      const functions = getFunctions();
      const createPortal = httpsCallable(functions, 'createCustomerPortalSession');
      const response = await createPortal({ 
        returnUrl: window.location.href 
      });
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error opening Stripe portal:', error);
      alert('Failed to open billing portal. Please try again or contact support.');
    }
  }

  function handleAccentColorChange(colorId) {
    setSelectedAccentColor(colorId);
    localStorage.setItem('fidora_accent_color', colorId);
    window.dispatchEvent(new CustomEvent('accentColorChange', { detail: colorId }));
  }

  const limits = TIER_LIMITS[subscriptionTier] || TIER_LIMITS.free;
  const usageData = usage?.usage || {};
  
  // Calculate usage percentages
  const textTokensPercent = limits.textTokens > 0 
    ? ((usageData.textTokensUsed || 0) / limits.textTokens) * 100 
    : 0;
  const voiceCharsPercent = limits.voiceCharacters > 0 
    ? ((usageData.voiceCharactersUsed || 0) / limits.voiceCharacters) * 100 
    : 0;
  const conversationsPercent = limits.conversations > 0 
    ? ((usageData.conversationCount || 0) / limits.conversations) * 100 
    : 0;
  const mockInterviewsPercent = limits.mockInterviews > 0 
    ? ((usageData.mockInterviewsUsed || 0) / limits.mockInterviews) * 100 
    : 0;

  // Convert voice characters to minutes (900 chars = 1 minute)
  const voiceMinutesUsed = Math.round((usageData.voiceCharactersUsed || 0) / 900);
  const voiceMinutesLimit = Math.round(limits.voiceCharacters / 900);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div style={{
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto',
      background: theme.bg,
      minHeight: '100vh',
      color: theme.text
    }}>
      <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '32px', color: theme.text }}>
        Settings
      </h1>

      {/* Profile Section */}
      <section style={{
        background: theme.card,
        borderRadius: '16px',
        marginBottom: '16px',
        border: `1px solid ${theme.border}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        <button
          onClick={() => toggleSection('profile')}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 32px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.text,
            textAlign: 'left'
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: theme.text }}>
            Profile
          </h2>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: expandedSections.profile ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        
        {expandedSections.profile && (
          <div style={{ padding: '0 32px 32px 32px' }}>
        
        {profileError && (
          <div style={{
            padding: '12px 16px',
            background: darkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
            border: `1px solid ${theme.error}`,
            borderRadius: '8px',
            marginBottom: '16px',
            color: theme.error
          }}>
            {profileError}
          </div>
        )}
        
        {profileSuccess && (
          <div style={{
            padding: '12px 16px',
            background: darkMode ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5',
            border: `1px solid ${theme.success}`,
            borderRadius: '8px',
            marginBottom: '16px',
            color: theme.success
          }}>
            {profileSuccess}
          </div>
        )}

        {/* Display Name */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: theme.text }}>
            Display Name
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              style={{
                flex: 1,
                padding: '10px 12px',
                background: darkMode ? 'rgba(30, 41, 59, 0.5)' : '#ffffff',
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px'
              }}
            />
            <button
              onClick={handleSaveDisplayName}
              disabled={savingProfile || !displayName.trim()}
              style={{
                padding: '10px 20px',
                background: theme.accent,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: savingProfile ? 'not-allowed' : 'pointer',
                opacity: savingProfile || !displayName.trim() ? 0.5 : 1,
                fontWeight: 600
              }}
            >
              Save
            </button>
          </div>
        </div>

        {/* Email */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: theme.text }}>
            Email
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="email"
              value={newEmail || email || ''}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Your email address"
              style={{
                flex: 1,
                padding: '10px 12px',
                background: darkMode ? 'rgba(30, 41, 59, 0.5)' : '#ffffff',
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px'
              }}
            />
            <button
              onClick={handleUpdateEmail}
              disabled={savingProfile || !newEmail.trim() || newEmail === email}
              style={{
                padding: '10px 20px',
                background: theme.accent,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: savingProfile ? 'not-allowed' : 'pointer',
                opacity: savingProfile || !newEmail.trim() || newEmail === email ? 0.5 : 1,
                fontWeight: 600
              }}
            >
              Save
            </button>
          </div>
        </div>

        {/* Change Password */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: theme.text }}>
            Change Password
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Current password"
              style={{
                padding: '10px 12px',
                background: darkMode ? 'rgba(30, 41, 59, 0.5)' : '#ffffff',
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px'
              }}
            />
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 6 characters)"
              style={{
                padding: '10px 12px',
                background: darkMode ? 'rgba(30, 41, 59, 0.5)' : '#ffffff',
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px'
              }}
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              style={{
                padding: '10px 12px',
                background: darkMode ? 'rgba(30, 41, 59, 0.5)' : '#ffffff',
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px'
              }}
            />
            <button
              onClick={handleChangePassword}
              disabled={savingProfile || !currentPassword || !newPassword || !confirmPassword}
              style={{
                padding: '10px 20px',
                background: theme.accent,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: savingProfile ? 'not-allowed' : 'pointer',
                opacity: savingProfile || !currentPassword || !newPassword || !confirmPassword ? 0.5 : 1,
                fontWeight: 600,
                alignSelf: 'flex-start'
              }}
            >
              Change Password
            </button>
          </div>
        </div>
          </div>
        )}
      </section>

      {/* Subscription Section */}
      <section style={{
        background: theme.card,
        borderRadius: '16px',
        marginBottom: '16px',
        border: `1px solid ${theme.border}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        <button
          onClick={() => toggleSection('subscription')}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 32px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.text,
            textAlign: 'left'
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: theme.text }}>
            Subscription
          </h2>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: expandedSections.subscription ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        
        {expandedSections.subscription && (
          <div style={{ padding: '0 32px 32px 32px' }}>
        
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <span style={{ fontWeight: 500, color: theme.text }}>Current Plan:</span>
            <span style={{
              padding: '6px 12px',
              background: theme.accent,
              color: 'white',
              borderRadius: '6px',
              fontWeight: 600,
              textTransform: 'uppercase'
            }}>
              {subscriptionTier || 'Free'}
            </span>
            <span style={{
              padding: '6px 12px',
              background: subscriptionStatus === 'active' ? theme.success : theme.error,
              color: 'white',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}>
              {subscriptionStatus === 'active' ? 'Active' : subscriptionStatus || 'Inactive'}
            </span>
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '12px', color: theme.text }}>
              Plan Limits:
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: theme.subtext }}>
              <li style={{ marginBottom: '8px' }}>
                • Text Tokens: {limits.textTokens.toLocaleString()} / month
              </li>
              <li style={{ marginBottom: '8px' }}>
                • Conversations: {limits.conversations} / month
              </li>
              <li style={{ marginBottom: '8px' }}>
                • Voice: {voiceMinutesLimit} minutes ({limits.voiceCharacters.toLocaleString()} characters) / month
              </li>
              <li style={{ marginBottom: '8px' }}>
                • Mock Interviews: {limits.mockInterviews} / month
              </li>
            </ul>
          </div>
          
          {(subscriptionTier === 'advisor' || subscriptionTier === 'coach' || subscriptionTier === 'mentor') && (
            <button
              onClick={handleOpenStripePortal}
              style={{
                padding: '12px 24px',
                background: theme.accent,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              Manage Billing & Cancel Subscription
            </button>
          )}
        </div>
          </div>
        )}
      </section>

      {/* Usage Summary Section */}
      <section style={{
        background: theme.card,
        borderRadius: '16px',
        marginBottom: '16px',
        border: `1px solid ${theme.border}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        <button
          onClick={() => toggleSection('usage')}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 32px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.text,
            textAlign: 'left'
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: theme.text }}>
            Usage Summary
          </h2>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: expandedSections.usage ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        
        {expandedSections.usage && (
          <div style={{ padding: '0 32px 32px 32px' }}>
        
        {loadingUsage ? (
          <div style={{ color: theme.subtext }}>Loading usage data...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Text Tokens */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 500, color: theme.text }}>Text Tokens</span>
                <span style={{ color: theme.subtext }}>
                  {(usageData.textTokensUsed || 0).toLocaleString()} / {limits.textTokens.toLocaleString()}
                </span>
              </div>
              <div style={{
                height: '8px',
                background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(textTokensPercent, 100)}%`,
                  background: textTokensPercent >= 80 ? theme.error : theme.accent,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ fontSize: '0.75rem', color: theme.subtext, marginTop: '4px' }}>
                {textTokensPercent.toFixed(1)}% used
              </div>
            </div>

            {/* Conversations */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 500, color: theme.text }}>Conversations</span>
                <span style={{ color: theme.subtext }}>
                  {usageData.conversationCount || 0} / {limits.conversations}
                </span>
              </div>
              <div style={{
                height: '8px',
                background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(conversationsPercent, 100)}%`,
                  background: conversationsPercent >= 80 ? theme.error : theme.accent,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ fontSize: '0.75rem', color: theme.subtext, marginTop: '4px' }}>
                {conversationsPercent.toFixed(1)}% used
              </div>
            </div>

            {/* Voice */}
            {limits.voiceCharacters > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 500, color: theme.text }}>Voice Minutes</span>
                  <span style={{ color: theme.subtext }}>
                    {voiceMinutesUsed} / {voiceMinutesLimit}
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(voiceCharsPercent, 100)}%`,
                    background: voiceCharsPercent >= 80 ? theme.error : theme.accent,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ fontSize: '0.75rem', color: theme.subtext, marginTop: '4px' }}>
                  {voiceCharsPercent.toFixed(1)}% used ({(usageData.voiceCharactersUsed || 0).toLocaleString()} / {limits.voiceCharacters.toLocaleString()} characters)
                </div>
              </div>
            )}

            {/* Mock Interviews */}
            {limits.mockInterviews > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 500, color: theme.text }}>Mock Interviews</span>
                  <span style={{ color: theme.subtext }}>
                    {usageData.mockInterviewsUsed || 0} / {limits.mockInterviews}
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(mockInterviewsPercent, 100)}%`,
                    background: mockInterviewsPercent >= 80 ? theme.error : theme.accent,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ fontSize: '0.75rem', color: theme.subtext, marginTop: '4px' }}>
                  {mockInterviewsPercent.toFixed(1)}% used
                </div>
              </div>
            )}
          </div>
        )}
          </div>
        )}
      </section>

      {/* Accent Colors Section */}
      <section style={{
        background: theme.card,
        borderRadius: '16px',
        marginBottom: '16px',
        border: `1px solid ${theme.border}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        <button
          onClick={() => toggleSection('accent')}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 32px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.text,
            textAlign: 'left'
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: theme.text }}>
            Chat Accent Color
          </h2>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: expandedSections.accent ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        
        {expandedSections.accent && (
          <div style={{ padding: '0 32px 32px 32px' }}>
            <p style={{ color: theme.subtext, marginBottom: '24px' }}>
              Choose the accent color for your messages in chat
            </p>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: '16px'
        }}>
          {accentColors.map((color) => {
            const isSelected = selectedAccentColor === color.id;
            return (
              <button
                key={color.id}
                onClick={() => handleAccentColorChange(color.id)}
                style={{
                  padding: '16px',
                  background: theme.card,
                  border: `3px solid ${isSelected ? color.primary : theme.border}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: isSelected 
                    ? `0 4px 12px ${color.primary}40`
                    : 'none'
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: color.gradient,
                  margin: '0 auto 8px',
                  boxShadow: `0 2px 8px ${color.primary}40`
                }} />
                <div style={{
                  textAlign: 'center',
                  fontWeight: isSelected ? 700 : 500,
                  color: theme.text,
                  fontSize: '0.875rem'
                }}>
                  {color.name}
                </div>
                {isSelected && (
                  <div style={{
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    color: color.primary,
                    marginTop: '4px',
                    fontWeight: 600
                  }}>
                    Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>
          </div>
        )}
      </section>

      {/* Theme Toggle Section */}
      <section style={{
        background: theme.card,
        borderRadius: '16px',
        marginBottom: '16px',
        border: `1px solid ${theme.border}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        <button
          onClick={() => toggleSection('appearance')}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 32px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.text,
            textAlign: 'left'
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: theme.text }}>
            Appearance
          </h2>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: expandedSections.appearance ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        
        {expandedSections.appearance && (
          <div style={{ padding: '0 32px 32px 32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label style={{ color: theme.text, fontWeight: 500 }}>Theme</label>
          <button
            onClick={onToggleTheme}
            style={{
              padding: '10px 20px',
              background: theme.accent,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600
            }}
          >
            {darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          </button>
          </div>
          </div>
        )}
      </section>

      {/* Notification Settings Section */}
      <section style={{
        background: theme.card,
        borderRadius: '16px',
        marginBottom: '16px',
        border: `1px solid ${theme.border}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        <button
          onClick={() => toggleSection('notifications')}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 32px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.text,
            textAlign: 'left'
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: theme.text }}>
            Coaching Reminders
          </h2>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: expandedSections.notifications ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        
        {expandedSections.notifications && (
          <div style={{ padding: '0 32px 32px 32px' }}>
            <NotificationSettings user={user} darkMode={darkMode} />
          </div>
        )}
      </section>

      {/* Document Management Section */}
      <section style={{
        background: theme.card,
        borderRadius: '16px',
        marginBottom: '16px',
        border: `1px solid ${theme.border}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease'
      }}>
        <button
          onClick={() => toggleSection('documents')}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 32px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: theme.text,
            textAlign: 'left'
          }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: theme.text }}>
            Documents & Transcripts
          </h2>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: expandedSections.documents ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        
        {expandedSections.documents && (
          <div style={{ padding: '0 32px 32px 32px' }}>
            <p style={{ color: theme.subtext, marginBottom: '16px' }}>
              Manage your resumes and chat transcripts. Enable memory to help your coach remember important details.
            </p>
            <DocumentManager user={user} darkMode={darkMode} />
          </div>
        )}
      </section>
    </div>
  );
}
